import { supabase } from '../config/supabase';
import { getApiBaseUrl } from '../utils/apiBase';

export interface SyncStatus {
  isDataFresh: boolean;
  lastSyncTime: Date | null;
  hoursAgo: number;
  needsSync: boolean;
}

export class SyncService {
  private static readonly SYNC_THRESHOLD_HOURS = 6; // Sync si han pasado más de 6 horas

  private static apiUrl(path: string): string {
    return `${getApiBaseUrl()}${path}`;
  }

  /**
   * Verificar si los datos necesitan sincronización
   */
  static async checkSyncStatus(storeId: string): Promise<SyncStatus> {
    try {
      const { data: syncLog, error } = await supabase
        .from('sync_logs')
        .select('completed_at, sync_type, status')
        .eq('store_id', storeId)
        .eq('status', 'success')
        .in('sync_type', ['initial', 'incremental', 'manual', 'login'])
        .order('completed_at', { ascending: false })
        .limit(1)
        .single();

      if (error && error.code !== 'PGRST116') {
        console.error('Error checking sync status:', error);
        return {
          isDataFresh: false,
          lastSyncTime: null,
          hoursAgo: 999,
          needsSync: true
        };
      }

      const completedAt = syncLog?.completed_at;
      const lastSyncTime = completedAt
        ? new Date(
            completedAt.endsWith('Z') || completedAt.includes('+') || completedAt.includes('-', 10)
              ? completedAt
              : `${completedAt}Z`
          )
        : null;
      
      // Debug logs para diagnosticar el problema de zona horaria
      console.log('🔍 DEBUG - Timestamp de la base de datos:', syncLog?.completed_at);
      console.log('🔍 DEBUG - lastSyncTime creado:', lastSyncTime);
      console.log('🔍 DEBUG - Hora actual (new Date()):', new Date());
      console.log('🔍 DEBUG - Hora actual (Date.now()):', Date.now());
      console.log('🔍 DEBUG - lastSyncTime.getTime():', lastSyncTime?.getTime());
      console.log('🔍 DEBUG - Diferencia en ms:', lastSyncTime ? (new Date().getTime() - lastSyncTime.getTime()) : 'N/A');
      
      const hoursAgo = lastSyncTime 
        ? (new Date().getTime() - lastSyncTime.getTime()) / (1000 * 60 * 60)
        : 999;
        
      console.log('🔍 DEBUG - hoursAgo calculado:', hoursAgo);

      return {
        isDataFresh: hoursAgo < this.SYNC_THRESHOLD_HOURS,
        lastSyncTime,
        hoursAgo,
        needsSync: hoursAgo >= this.SYNC_THRESHOLD_HOURS
      };

    } catch (error) {
      console.error('Error in checkSyncStatus:', error);
      return {
        isDataFresh: false,
        lastSyncTime: null,
        hoursAgo: 999,
        needsSync: true
      };
    }
  }

  /**
   * Ejecutar sincronización (con continuación automática si hay lotes parciales)
   */
  static async triggerAutoSync(storeId: string): Promise<{ success: boolean; message: string }> {
    try {
      console.log(`🔄 Iniciando sync automático para tienda ${storeId}`);

      let resumeNextUrl: string | undefined;
      let batch = 0;
      const MAX_BATCHES = 100;
      let totalOrders = 0;

      do {
        batch++;
        const response = await fetch(this.apiUrl(`/api/sync/${storeId}`), {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            type: 'incremental',
            resumeNextUrl
          })
        });

        const result = await response.json().catch(() => ({}));

        if (!response.ok || !result.success) {
          const detail = result.error || response.statusText || 'Error desconocido';
          throw new Error(
            batch > 1
              ? `Error en lote ${batch} (tras ${totalOrders} órdenes): ${detail}`
              : `Error en sync unificado: ${detail}`
          );
        }

        totalOrders += result.data?.tiendanube?.recordsProcessed || 0;
        resumeNextUrl = result.partial && result.hasMore ? result.nextPageUrl : undefined;

        if (resumeNextUrl) {
          console.log(`📦 Lote ${batch} completado (${totalOrders} órdenes). Continuando catch-up...`);
        }
      } while (resumeNextUrl && batch < MAX_BATCHES);

      console.log(`✅ Sync automático completado para tienda ${storeId} (${batch} lotes, ${totalOrders} órdenes)`);
      return {
        success: true,
        message: batch > 1
          ? `Datos actualizados (${batch} lotes, ${totalOrders} órdenes)`
          : 'Datos actualizados correctamente'
      };

    } catch (error: any) {
      console.error('Error en triggerAutoSync:', error);
      return {
        success: false,
        message: error.message || 'Error actualizando datos'
      };
    }
  }

  /**
   * Sync inteligente: solo si es necesario
   */
  static async smartSync(storeId: string): Promise<{ wasNeeded: boolean; result?: any }> {
    const status = await this.checkSyncStatus(storeId);
    
    if (status.needsSync) {
      console.log(`📊 Datos antiguos (${status.hoursAgo.toFixed(1)}h). Iniciando sync...`);
      const result = await this.triggerAutoSync(storeId);
      return { wasNeeded: true, result };
    } else {
      console.log(`✅ Datos frescos (${status.hoursAgo.toFixed(1)}h). No es necesario sync.`);
      return { wasNeeded: false };
    }
  }

  /**
   * Obtener logs de sincronización
   */
  static async getSyncStats(storeId: string) {
    try {
      const response = await fetch(this.apiUrl(`/api/sync/logs/${storeId}`));
      if (!response.ok) {
        throw new Error('Error obteniendo logs de sincronización');
      }
      
      const data = await response.json();
      return data;
    } catch (error) {
      console.error('Error getting sync stats:', error);
      return null;
    }
  }
}
